/**
 * スペーシング定数
 * アプリケーション全体で使用する余白・間隔の基準値を定義
 * 4の倍数を基本単位として統一
 *
 * @example
 * // Tailwindクラスでの使用
 * import { SPACING_CLASSES } from '@/shared/constants/spacing';
 * <div className={SPACING_CLASSES.LG.padding}>...</div>
 *
 * @example
 * // インラインスタイルでの使用（動的な値が必要な場合のみ）
 * import { SPACING } from '@/shared/constants/spacing';
 * <div style={{ padding: SPACING.LG }}>...</div>
 *
 * @example
 * // Ant Design Gridのガター設定
 * import { GUTTER_CONFIG } from '@/shared/constants/spacing';
 * <Row gutter={GUTTER_CONFIG.medium}>...</Row>
 *
 * @example
 * // ボーダー半径の使用
 * import { BORDER_RADIUS } from '@/shared/constants/spacing';
 * <Card style={{ borderRadius: BORDER_RADIUS.lg }}>...</Card>
 */

/**
 * 基本スペーシング値（px）
 */
export const SPACING = {
  /** 4px - 最小スペーシング */
  XXXS: 4,
  /** 8px - 極小スペーシング */
  XXS: 8,
  /** 12px - 小スペーシング */
  XS: 12,
  /** 16px - 中スペーシング */
  SM: 16,
  /** 20px - やや大きいスペーシング */
  MD: 20,
  /** 24px - 大スペーシング */
  LG: 24,
  /** 32px - 極大スペーシング */
  XL: 32,
  /** 48px - 最大スペーシング */
  XXL: 48,
} as const;

/**
 * Tailwindクラス用のスペーシング定義
 * Tailwindのユーティリティクラスとの対応
 */
export const SPACING_CLASSES = {
  XXXS: {
    padding: 'p-1',
    paddingX: 'px-1',
    paddingY: 'py-1',
    margin: 'm-1',
    marginX: 'mx-1',
    marginY: 'my-1',
    gap: 'gap-1',
  },
  XXS: {
    padding: 'p-2',
    paddingX: 'px-2',
    paddingY: 'py-2',
    margin: 'm-2',
    marginX: 'mx-2',
    marginY: 'my-2',
    gap: 'gap-2',
  },
  XS: {
    padding: 'p-3',
    paddingX: 'px-3',
    paddingY: 'py-3',
    margin: 'm-3',
    marginX: 'mx-3',
    marginY: 'my-3',
    gap: 'gap-3',
  },
  SM: {
    padding: 'p-4',
    paddingX: 'px-4',
    paddingY: 'py-4',
    margin: 'm-4',
    marginX: 'mx-4',
    marginY: 'my-4',
    gap: 'gap-4',
  },
  MD: {
    padding: 'p-5',
    paddingX: 'px-5',
    paddingY: 'py-5',
    margin: 'm-5',
    marginX: 'mx-5',
    marginY: 'my-5',
    gap: 'gap-5',
  },
  LG: {
    padding: 'p-6',
    paddingX: 'px-6',
    paddingY: 'py-6',
    margin: 'm-6',
    marginX: 'mx-6',
    marginY: 'my-6',
    gap: 'gap-6',
  },
  XL: {
    padding: 'p-8',
    paddingX: 'px-8',
    paddingY: 'py-8',
    margin: 'm-8',
    marginX: 'mx-8',
    marginY: 'my-8',
    gap: 'gap-8',
  },
  XXL: {
    padding: 'p-12',
    paddingX: 'px-12',
    paddingY: 'py-12',
    margin: 'm-12',
    marginX: 'mx-12',
    marginY: 'my-12',
    gap: 'gap-12',
  },
} as const;

/**
 * Ant Design Grid用のガター設定
 */
export const GUTTER_CONFIG = {
  /** 8px - 小さいガター */
  small: [8, 8] as [number, number],
  /** 16px - 中ガター（デフォルト推奨） */
  medium: [16, 16] as [number, number],
  /** 24px - 大きいガター */
  large: [24, 24] as [number, number],
  /** 32px - 極大ガター */
  extraLarge: [32, 32] as [number, number],
} as const;

/**
 * コンポーネント固有のスペーシング設定
 */
export const COMPONENT_SPACING = {
  /** レイアウト用スペーシング */
  layout: {
    contentPadding: {
      mobile: SPACING.XS,
      desktop: SPACING.LG,
    },
    contentMargin: {
      mobile: SPACING.XS,
      desktop: SPACING.LG,
    },
  },
  /** カード用スペーシング */
  card: {
    padding: SPACING.SM,
    margin: SPACING.SM,
  },
  /** フォーム用スペーシング */
  form: {
    itemMarginBottom: SPACING.SM,
    labelMarginBottom: SPACING.XXS,
  },
  /** テーブル用スペーシング */
  table: {
    cellPadding: SPACING.XXS,
    marginBottom: SPACING.SM,
  },
} as const;

/**
 * ボーダー半径（border-radius）設定
 */
export const BORDER_RADIUS = {
  /** 0px - 角なし */
  none: 0,
  /** 2px - 最小丸み */
  xs: 2,
  /** 4px - 小丸み */
  sm: 4,
  /** 6px - 中丸み */
  md: 6,
  /** 8px - 大丸み */
  lg: 8,
  /** 12px - 極大丸み */
  xl: 12,
  /** 9999px - 完全な円形 */
  full: 9999,
} as const;

/**
 * スペーシングユーティリティ関数
 */
export const SpacingUtils = {
  /**
   * スペーシング値をrem単位に変換
   */
  pxToRem: (px: number): string => {
    return `${px / 16}rem`;
  },

  /**
   * パディングスタイルオブジェクトを生成
   */
  getPaddingStyle: (size: keyof typeof SPACING): { padding: string } => {
    return {
      padding: `${SPACING[size]}px`,
    };
  },

  /**
   * マージンスタイルオブジェクトを生成
   */
  getMarginStyle: (size: keyof typeof SPACING): { margin: string } => {
    return {
      margin: `${SPACING[size]}px`,
    };
  },

  /**
   * ガタースタイルオブジェクトを生成
   */
  getGutterStyle: (size: keyof typeof GUTTER_CONFIG): [number, number] => {
    return GUTTER_CONFIG[size];
  },
} as const;
